import { DateTime } from "luxon";
import { useMemo } from "react";

export enum DateFilterPreset {
  /** Showing only captures taken the last day */
  lastCapture = "Last capture",
  /** Showing only captures taken this week */
  thisWeek = "This week",
  /** Showing only captures taken this month */
  thisMonth = "This month",
}

/**
 * @returns The date range a quick preset stands for
 * @param preset The preset the user picked
 * @param lastCaptureDate The date of the newest capture in the project
 */
export function rangeForPreset(
  preset: DateFilterPreset,
  lastCaptureDate: Date | undefined,
): [start: Date, end: Date] {
  if (preset === DateFilterPreset.lastCapture && lastCaptureDate) {
    return [lastCaptureDate, lastCaptureDate];
  }
  return [
    DateTime.now().startOf(presetUnit(preset)).toJSDate(),
    DateTime.now().endOf("day").toJSDate(),
  ];
}

/**
 * The store keeps only a start/end pair, so which preset looks selected has to be derived
 * back from the range.
 *
 * @param selectedStartDate The currently selected start date
 * @param selectedEndDate The currently selected end date
 * @param lastCaptureDate The date of the newest capture in the project
 * @returns The preset the range corresponds to, or null when it matches none
 */
export function getSelectedPreset(
  selectedStartDate: Date | undefined,
  selectedEndDate: Date | undefined,
  lastCaptureDate: Date | undefined,
): DateFilterPreset | null {
  if (!selectedStartDate || !selectedEndDate) return null;

  const now = DateTime.now();
  const lastCaptureDateTime = lastCaptureDate
    ? DateTime.fromJSDate(lastCaptureDate)
    : now;
  const start = DateTime.fromJSDate(selectedStartDate);
  const end = DateTime.fromJSDate(selectedEndDate);

  if (
    end.hasSame(lastCaptureDateTime, "day") &&
    start.hasSame(lastCaptureDateTime, "day")
  ) {
    return DateFilterPreset.lastCapture;
  }

  if (!end.hasSame(now, "day")) return null;
  if (start.hasSame(now.startOf("week"), "day")) {
    return DateFilterPreset.thisWeek;
  }
  if (start.hasSame(now.startOf("month"), "day")) {
    return DateFilterPreset.thisMonth;
  }
  return null;
}

/**
 * @returns The preset the selected date range corresponds to, or null when it matches none
 * @param selectedStartDate The currently selected start date
 * @param selectedEndDate The currently selected end date
 * @param lastCaptureDate The date of the newest capture in the project
 */
export function useSelectedPreset(
  selectedStartDate: Date | undefined,
  selectedEndDate: Date | undefined,
  lastCaptureDate: Date | undefined,
): DateFilterPreset | null {
  return useMemo(
    () =>
      getSelectedPreset(selectedStartDate, selectedEndDate, lastCaptureDate),
    [selectedStartDate, selectedEndDate, lastCaptureDate],
  );
}

/**
 * When setting the 'from' date, this function checks whether the 'to' date should be set to the last capture date.
 *
 * @param date The 'from' date being set
 * @param selectedEndDate The currently selected 'to' date
 * @param lastCaptureDate The date of the last capture in the project
 * @returns whether the 'to' date should be set to the last capture date.
 */
export function shouldSetLastCaptureDate(
  date: Date | undefined,
  selectedEndDate: Date | undefined,
  lastCaptureDate: Date | undefined,
): boolean {
  return (
    !!date && !selectedEndDate && !!lastCaptureDate && date < lastCaptureDate
  );
}

function presetUnit(preset: DateFilterPreset): "day" | "week" | "month" {
  switch (preset) {
    case DateFilterPreset.lastCapture:
      return "day";
    case DateFilterPreset.thisWeek:
      return "week";
    case DateFilterPreset.thisMonth:
      return "month";
  }
}
